import React, { useEffect, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromHTML,
  Modifier,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Form } from 'react-bootstrap';
import CValidator from '../CValidator';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { isNullUndefined } from '../../../common/Helpers';

function decodeEntities(str) {
  if (!str) return '';
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return doc.documentElement.textContent || '';
}

function stripTags(str) {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, ' ');
}

function normalizePlainText(text, { collapseNewlinesToSpace = true } = {}) {
  if (!text) return '';
  let out = text
    .replace(/\u00A0/g, ' ')
    .replace(/&(nbsp|#160);/gi, ' ');

  // colapsa espacios/tabs múltiples
  out = out.replace(/[ \t]+/g, ' ');

  if (collapseNewlinesToSpace) {
    out = out.replace(/\s*\r?\n\s*/g, ' ');
  } else {
    out = out.replace(/\r?\n{3,}/g, '\n\n');
  }

  return out.replace(/[ ]{2,}/g, ' ').trim();
}

function htmlToPlainText(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let text = doc.body.innerText || doc.body.textContent || '';
  text = text.replace(/\u00a0/g, ' ').replace(/[ \t]+/g, ' ');
  text = text.replace(/\n{3,}/g, '\n\n');
  return text.trim();
}

export default function CTextArea(props) {
  const inputParam_LabelText = props.labelText;
  const inputParam_ControlValue = props.controlValue !== null && props.controlValue !== undefined ? props.controlValue : '';
  const inputParam_HideToolbar = props.hideToolbar !== null && props.hideToolbar !== undefined ? props.hideToolbar : false;
  const inputParam_ClassName = props.className !== null && props.className !== undefined ? props.className : '';
  const inputParam_SetterControlValue = props.setterControlValue;
  const inputParam_IsInvalirControl = props.isInvalirControl;
  const inputParam_MaxLength = props.maxLength !== null && props.maxLength !== undefined ? props.maxLength : null;
  const inputParam_HideToolbarOptions = props.hideToolbarOptions !== null && props.hideToolbarOptions !== undefined ? props.hideToolbarOptions : [];

  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(
      ContentState.createFromBlockArray(convertFromHTML(inputParam_ControlValue))
    )
  );
  const [isInvalidControl, setIsInvalidControl] = useState(false);

  const isInValidFormControl = (value) => (value === '' ? true : false);

  const checkIfIsInvalidControl = () => {
    const _isInvalidControl = isInvalidControl ?? false;
    const _inputParam_IsInvalirControl = inputParam_IsInvalirControl ?? false;
    return _isInvalidControl === true || _inputParam_IsInvalirControl === true;
  };

  const onChangeValue = (st) => {
    const controlValue = draftToHtml(convertToRaw(st.getCurrentContent()));
    setEditorState(st);
    setIsInvalidControl(isInValidFormControl(controlValue));
    inputParam_SetterControlValue && inputParam_SetterControlValue(controlValue);
  };

  const handleBeforeInput = (input) => {
    if (inputParam_MaxLength == null) return 'not-handled';
    const currentContent = editorState.getCurrentContent();
    const currentLength = currentContent.getPlainText('').length;
    if (currentLength >= inputParam_MaxLength) return 'handled';
    return 'not-handled';
  };

  const handlePastedText = (pastedText, html) => {
    let plain = '';
    if (html && html.trim()) {
        plain = htmlToPlainText(html);
    } else if (pastedText && pastedText.trim()) {
        const decoded = decodeEntities(pastedText);
        plain = stripTags(decoded);
    } else {
        return 'handled';
    }

    plain = normalizePlainText(plain, { collapseNewlinesToSpace: true });

    if (inputParam_MaxLength != null) {
        const currentLen = editorState.getCurrentContent().getPlainText('').length;
        const remaining = inputParam_MaxLength - currentLen;
        if (remaining <= 0) return 'handled';
        if (plain.length > remaining) plain = plain.slice(0, remaining);
    }

    const content = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const newContent = Modifier.replaceText(content, selection, plain);
    const newState = EditorState.push(editorState, newContent, 'insert-characters');

    setEditorState(newState);

    const controlValue = draftToHtml(convertToRaw(newState.getCurrentContent()));
    inputParam_SetterControlValue && inputParam_SetterControlValue(controlValue);

    return 'handled';
  };


const linkOptions = ['link', 'unlink'].filter(
  option => !inputParam_HideToolbarOptions.includes(option)
);

const toolbarOptions = [
  'inline',
  'blockType',
  'fontSize',
  'list',
  'textAlign',
  'colorPicker',
  'link',
  'image',
  'remove',
  'history',
].filter(option => {
  // image y remove son opciones principales
  if (option === 'image' && inputParam_HideToolbarOptions.includes('image')) {
    return false;
  }

  if (option === 'remove' && inputParam_HideToolbarOptions.includes('remove')) {
    return false;
  }

  // Si ocultamos link Y unlink, no tiene sentido mostrar el grupo link
  if (option === 'link' && linkOptions.length === 0) {
    return false;
  }

  return true;
});

const toolbarConfig = inputParam_HideToolbar
  ? undefined
  : {
      options: toolbarOptions,

      inline: {
        options: ['bold', 'italic']
      },

      link: {
        options: linkOptions
      }
    };

  useEffect(() => {
    try {
      if (!isNullUndefined(props.clearData)) {
        if (props.clearData === true) {
          setTimeout(() => {
            props.setClearDate(false);
            setEditorState(EditorState.createEmpty());
          }, "100");
        }
      }
    } catch (error) {
      
    }
  }, [props.clearData]);
  return (
    <>
      <b>
        <Form.Label className="pe-2">{inputParam_LabelText}:</Form.Label>
      </b>

      <Editor
        editorState={editorState}
        onEditorStateChange={onChangeValue}
        wrapperClassName={'demo-wrapper ' + inputParam_ClassName}
        editorClassName="demo-editor"
        toolbarHidden={inputParam_HideToolbar}
        toolbar={toolbarConfig}
        handleBeforeInput={handleBeforeInput}
        handlePastedText={handlePastedText}
        stripPastedStyles={true} // <- este prop existe y suele bastar para quitar estilos
        // “cinturón y tirantes”: pasarlo también al Draft interno + un onPaste DOM para debug
        editorProps={{
          handlePastedText, // por si tu versión lo requiere aquí
          stripPastedStyles: true,
          onPaste: (e) => {
            // quita esto tras verificar que el evento llega
            // console.log('DOM onPaste types:', e?.clipboardData?.types);
          },
        }}
      />

      <CValidator isInValid={checkIfIsInvalidControl()} />
    </>
  );
}